/**
 * SharedComponentRenderer — thin dispatcher for the visual editor.
 *
 * Delegates to category-specific renderer files under ./renderers/:
 *   TextRenderers        → TEXT, DATA_TEXT
 *   FormControlRenderers → BUTTON, INPUT
 *   MediaRenderers       → IMAGE, ICON
 *   LayoutRenderers      → CONTAINER, FLEX_CONTAINER, CARD, FORM_CONTAINER, MODAL, LIST
 *   ChartRenderer        → CHART + all chart variants
 *   TableRenderer        → TABLE, ADVANCED_TABLE
 *   NavigationRenderers  → BREADCRUMB, CUSTOM
 *   FileStorageRenderer  → FILE_STORAGE
 *
 * IoT controls (RADIAL_DIAL … THERMOSTAT) and collaboration / productivity
 * widgets (KANBAN_BOARD, CHECKLIST, etc.) are one-line delegations to their
 * own existing Shared* / control component files and need no separate renderer.
 *
 * Roadmap: Documents/Roadmaps/Simplification_14_SharedComponentRenderer_Decomposition_Roadmap.md
 */
import React, { useMemo } from "react";
import { Component, ComponentType, Viewport } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { withNormalizedComponentStyle } from "@/utils/component-style-normalization";

// ── Renderer files ─────────────────────────────────────────────────────────────
import { TextRenderers } from "./renderers/TextRenderers";
import { FormControlRenderers } from "./renderers/FormControlRenderers";
import { MediaRenderers } from "./renderers/MediaRenderers";
import { LayoutRenderers } from "./renderers/LayoutRenderers";
import { ChartRenderer } from "./renderers/ChartRenderer";
import { TableRenderer } from "./renderers/TableRenderer";
import { NavigationRenderers } from "./renderers/NavigationRenderers";
import { FileStorageRenderer } from "./renderers/FileStorageRenderer";
import { resolveViewerMode } from "./renderers/utils";

// ── IoT / control components (no separate renderer needed — one-liners) ────────
import { RadialDial } from "@/components/controls/RadialDial";
import { ToggleSwitch } from "@/components/controls/ToggleSwitch";
import { StatusLight } from "@/components/controls/StatusLight";
import { PushButton } from "@/components/controls/PushButton";
import { IconButton } from "@/components/controls/IconButton";
import { Stepper } from "@/components/controls/Stepper";
import { Slider as SliderControl } from "@/components/controls/Slider";
import { LevelIndicator } from "@/components/controls/LevelIndicator";
import { SegmentedControl } from "@/components/controls/SegmentedControl";
import { ColorPicker } from "@/components/controls/ColorPicker";
import { Thermostat } from "@/components/controls/Thermostat";

// ── Collaboration / productivity widgets (no separate renderer needed) ─────────
import { SharedKanbanBoardComponent } from "./SharedKanbanBoardComponent";
import { SharedChecklistComponent } from "./SharedChecklistComponent";
import { SharedHorizontalTimelineComponent } from "./SharedHorizontalTimelineComponent";
import { SharedAssignmentWidgetComponent } from "./SharedAssignmentWidgetComponent";
import { SharedGanttChartComponent } from "./SharedGanttChartComponent";
import { SharedCalendarComponent } from "./SharedCalendarComponent";
import { SharedTimeTrackerComponent } from "./SharedTimeTrackerComponent";
import { SharedProjectHubComponent } from "./SharedProjectHubComponent";
import { SharedQuestionnaireComponent } from "./SharedQuestionnaireComponent";
import { SharedVotingCardComponent } from "./SharedVotingCardComponent";

// ─────────────────────────────────────────────────────────────────────────────
// Public interfaces
// ─────────────────────────────────────────────────────────────────────────────

/** Performance hints for optional viewport-culling optimisation. */
export interface PerformanceHints {
  enableVirtualization?: boolean;
  /** Typed as unknown to avoid an `any` cast; will be SpatialIndexingSystem. */
  spatialIndex?: unknown;
  viewportBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/** Props accepted by the SharedComponentRenderer dispatcher. */
export interface SharedComponentRendererProps {
  component: Component;
  performanceHints?: PerformanceHints;
  /** All components in the project/page — used for expression evaluation. */
  allComponents?: Component[];
  /** Current viewport for responsive rendering. */
  viewport?: Viewport;
  /** Current render color scheme. Omit to preserve legacy base/breakpoint rendering. */
  activeColorScheme?: ColorScheme;
}

// ─────────────────────────────────────────────────────────────────────────────
// UnknownComponentFallback
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rendered by the exhaustiveness-checked default arm.
 * Visible in development so engineers notice immediately when a new
 * ComponentType enum value is added without a corresponding switch arm.
 */
const UnknownComponentFallback: React.FC<{ componentType: string }> = ({
  componentType,
}) => (
  <div className="w-full h-full flex items-center justify-center bg-red-50 border border-red-200 rounded">
    <span className="text-red-500">Unknown: {componentType}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SharedComponentRenderer — main dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export const SharedComponentRenderer: React.FC<SharedComponentRendererProps> = ({
  component,
  performanceHints,
  allComponents,
  viewport = Viewport.DESKTOP,
  activeColorScheme,
}) => {
  const renderComponent = useMemo(
    () => withNormalizedComponentStyle(component, viewport, activeColorScheme),
    [component, viewport, activeColorScheme],
  );

  // Viewport culling: skip off-screen components when virtualization is enabled.
  const shouldRender = useMemo(() => {
    if (
      !performanceHints?.enableVirtualization ||
      !performanceHints.viewportBounds
    ) {
      return true;
    }
    if (!renderComponent.position || !renderComponent.size) return true;

    const width =
      typeof renderComponent.size.width === "number" ? renderComponent.size.width : 0;
    const height =
      typeof renderComponent.size.height === "number" ? renderComponent.size.height : 0;
    if (width === 0 || height === 0) return true;

    const bounds = performanceHints.viewportBounds;
    const compLeft = renderComponent.position.x ?? 0;
    const compTop = renderComponent.position.y ?? 0;
    return !(
      compLeft + width < bounds.x ||
      compLeft > bounds.x + bounds.width ||
      compTop + height < bounds.y ||
      compTop > bounds.y + bounds.height
    );
  }, [renderComponent, performanceHints]);

  if (!shouldRender) return null;

  let content: React.ReactElement | null = null;

  switch (renderComponent.type) {
    // ── Text ──────────────────────────────────────────────────────────────────
    case ComponentType.TEXT:
    case ComponentType.DATA_TEXT:
      content = (
        <TextRenderers
          component={renderComponent}
          viewport={viewport}
          allComponents={allComponents}
        />
      );
      break;

    // ── Form controls ─────────────────────────────────────────────────────────
    case ComponentType.BUTTON:
    case ComponentType.INPUT:
      content = (
        <FormControlRenderers component={renderComponent} viewport={viewport} />
      );
      break;

    // ── Media / icon ──────────────────────────────────────────────────────────
    case ComponentType.IMAGE:
    case ComponentType.ICON:
      content = <MediaRenderers component={renderComponent} />;
      break;

    // ── Layout containers ─────────────────────────────────────────────────────
    case ComponentType.CONTAINER:
    case ComponentType.FLEX_CONTAINER:
    case ComponentType.CARD:
    case ComponentType.FORM_CONTAINER:
    case ComponentType.MODAL:
    case ComponentType.LIST:
      content = (
        <LayoutRenderers
          component={renderComponent}
          allComponents={allComponents}
          viewport={viewport}
          activeColorScheme={activeColorScheme}
        />
      );
      break;

    // ── Charts ────────────────────────────────────────────────────────────────
    case ComponentType.CHART:
    case ComponentType.BAR_CHART:
    case ComponentType.LINE_CHART:
    case ComponentType.PIE_CHART:
    case ComponentType.SCATTER_CHART:
    case ComponentType.AREA_CHART:
    case ComponentType.HEATMAP_CHART:
    case ComponentType.TREEMAP_CHART:
    case ComponentType.GAUGE_CHART:
    case ComponentType.VERTICAL_GAUGE_CHART:
    case ComponentType.DATA_CARD:
    case ComponentType.FERMENTATION_TANK_CARD:
    case ComponentType.MAP_CHART:
      content = <ChartRenderer component={renderComponent} viewport={viewport} />;
      break;

    // ── Tables ────────────────────────────────────────────────────────────────
    case ComponentType.TABLE:
    case ComponentType.ADVANCED_TABLE:
      content = <TableRenderer component={renderComponent} viewport={viewport} />;
      break;

    // ── Navigation / custom HTML ──────────────────────────────────────────────
    case ComponentType.BREADCRUMB:
    case ComponentType.CUSTOM:
      content = (
        <NavigationRenderers component={renderComponent} viewport={viewport} />
      );
      break;

    // ── File storage ──────────────────────────────────────────────────────────
    case ComponentType.FILE_STORAGE:
      content = <FileStorageRenderer component={renderComponent} viewport={viewport} />;
      break;

    // ── IoT / hardware controls ───────────────────────────────────────────────
    case ComponentType.RADIAL_DIAL:
      content = <RadialDial component={renderComponent} />;
      break;
    case ComponentType.TOGGLE_SWITCH:
      content = <ToggleSwitch component={renderComponent} />;
      break;
    case ComponentType.STATUS_LIGHT:
      content = <StatusLight component={renderComponent} />;
      break;
    case ComponentType.PUSH_BUTTON:
      content = <PushButton component={renderComponent} />;
      break;
    case ComponentType.ICON_BUTTON:
      content = <IconButton component={renderComponent} />;
      break;
    case ComponentType.STEPPER:
      content = <Stepper component={renderComponent} />;
      break;
    case ComponentType.SLIDER:
      content = <SliderControl component={renderComponent} />;
      break;
    case ComponentType.LEVEL_INDICATOR:
      content = <LevelIndicator component={renderComponent} />;
      break;
    case ComponentType.SEGMENTED_CONTROL:
      content = <SegmentedControl component={renderComponent} />;
      break;
    case ComponentType.COLOR_PICKER:
      content = <ColorPicker component={renderComponent} />;
      break;
    case ComponentType.THERMOSTAT:
      content = <Thermostat component={renderComponent} />;
      break;

    // ── Collaboration / productivity widgets ──────────────────────────────────
    // resolveViewerMode() centralises the viewer/mobile/tablet detection logic.
    case ComponentType.KANBAN_BOARD:
      content = (
        <SharedKanbanBoardComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.CHECKLIST:
      content = (
        <SharedChecklistComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.HORIZONTAL_TIMELINE:
      content = (
        <SharedHorizontalTimelineComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.ASSIGNMENT_WIDGET:
      content = (
        <SharedAssignmentWidgetComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.GANTT_CHART:
      content = (
        <SharedGanttChartComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.CALENDAR:
      content = (
        <SharedCalendarComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.TIME_TRACKER:
      content = (
        <SharedTimeTrackerComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.PROJECT_HUB:
      content = (
        <SharedProjectHubComponent
          component={renderComponent}
          isViewerMode={resolveViewerMode(viewport)}
        />
      );
      break;
    case ComponentType.QUESTIONNAIRE: {
      // Template viewer paths are excluded — questionnaire should not activate
      // interactive mode when previewing a template.
      const isQuestionnaireViewerMode =
        resolveViewerMode(viewport) &&
        (typeof window === "undefined" ||
          !window.location.pathname.includes("/viewer/template"));
      content = (
        <SharedQuestionnaireComponent
          component={renderComponent}
          isViewerMode={isQuestionnaireViewerMode}
        />
      );
      break;
    }
    case ComponentType.VOTING_CARD: {
      // Template viewer paths are excluded — same reasoning as QUESTIONNAIRE.
      const isVotingViewerMode =
        typeof window !== "undefined" &&
        window.location.pathname.includes("/viewer/") &&
        !window.location.pathname.includes("/viewer/template");
      content = (
        <SharedVotingCardComponent
          component={renderComponent}
          isViewerMode={isVotingViewerMode}
        />
      );
      break;
    }

    // ── Exhaustiveness check ──────────────────────────────────────────────────
    default: {
      // TypeScript will produce a type error on the next line if a new
      // ComponentType enum value is added without a corresponding case arm.
      const _exhaustive: never = renderComponent.type;
      console.warn(
        `⚠️ [SharedComponentRenderer] Unknown component type: ${String(_exhaustive)} for component ${renderComponent.id}`,
      );
      content = (
        <UnknownComponentFallback componentType={String(_exhaustive)} />
      );
    }
  }

  // Wrap with data attributes only (no positioning).
  // Positioning is handled by parent components like UnifiedComponentWrapper.
  // Text, buttons, and breadcrumbs fit their content (no h-full); others fill.
  const shouldFitContent =
    renderComponent.type === ComponentType.TEXT ||
    renderComponent.type === ComponentType.DATA_TEXT ||
    renderComponent.type === ComponentType.BUTTON ||
    renderComponent.type === ComponentType.BREADCRUMB;

  return (
    <div
      data-component-id={renderComponent.id}
      data-component-type={renderComponent.type}
      className={shouldFitContent ? "w-full" : "w-full h-full"}
    >
      {content}
    </div>
  );
};

export default SharedComponentRenderer;
